import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  FlatList,
  Animated,
  Platform,
  Image,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../../services/AuthContext';
import { getUserByClerkId, getUserById, createGame, findGamesBetweenPlayers, updateGame } from '../../services/database';
import { getMessages, subscribeToConversation, sendMessage as sendMessageSupabase, markMessagesAsRead } from '../../services/chatService';
import GameTrackingView from '../GameTrackingView';
import NotificationService from '../../services/NotificationService';
import { useChatCache } from '../../contexts/ChatCacheContext';
import { getColors, typography, spacing, borderRadius, getShadows } from '../../styles/theme';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import LoadingAnimation from '../LoadingAnimation';
import { getProfilePictureUrl } from '../../services/storage';

// Chess and common emojis for quick access
const QUICK_EMOJIS = [
  '♟️', '♞', '♝', '♜', '♛', '♚', // Chess pieces
  '🏆', '🎯', '⚔️', '🤝', '👏', '🔥', // Competition/game related
  '😊', '😄', '👍', '❤️', '🎉', '✨', // Common reactions
];

const ChatDetailPanel = ({ conversation, otherUser, navigation, isMobile = false, onBack }) => {
  const { user, appUser, clerkUserId } = useAuth();
  const { getCachedConversation } = useChatCache();
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [activeGameId, setActiveGameId] = useState(null);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [pendingGameRequest, setPendingGameRequest] = useState(null);
  const [creatingGame, setCreatingGame] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(conversation?.id);
  const [headerAvatarError, setHeaderAvatarError] = useState(false);
  const flatListRef = useRef(null);
  const subscriptionRef = useRef(null);
  const messageAnimations = useRef({});
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    initializeChat();
    
    return () => {
      if (subscriptionRef.current) {
        // Supabase subscriptions return a function directly, AppSync returns an object with unsubscribe
        if (typeof subscriptionRef.current === 'function') {
          subscriptionRef.current();
        } else if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
          subscriptionRef.current.unsubscribe();
        }
      }
      // Clear scroll timeout on unmount
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [conversation?.id]);

  const initializeChat = async () => {
    if (!conversation) {
      console.warn('⚠️ Cannot initialize chat: conversation is missing');
      return;
    }

    if (!conversation.id) {
      console.error('❌ Cannot initialize chat: conversation.id is missing');
      return;
    }

    console.log('🚀 Initializing chat for conversation:', conversation.id);

    const userDbId = await fetchCurrentUserDbId();
    if (!userDbId) {
      console.error('❌ Cannot initialize chat: userDbId is missing');
      return;
    }

    console.log('✅ User DB ID:', userDbId);

    await checkForActiveGame(userDbId);
    await checkForPendingGameRequest(userDbId);
    
    // If conversation is a draft, don't fetch messages yet (there are none)
    if (!conversation.id.startsWith('draft-')) {
      await fetchMessages(conversation.id);
      subscribeToMessages(conversation.id);
    } else {
      console.log('📝 Draft conversation - skipping message fetch');
    }
    
    // Poll for game state changes every 3 seconds
    const gameStateInterval = setInterval(async () => {
      if (userDbId) {
        // Check for active game (to detect if game started)
        await checkForActiveGame(userDbId);
        // Check for pending requests (if no active game)
        await checkForPendingGameRequest(userDbId);
      }
    }, 3000);
    
    return () => clearInterval(gameStateInterval);
  };

  // Check for pending game requests (opponent ready but game not started)
  const checkForPendingGameRequest = async (userDbId) => {
    if (!otherUser?.id) return;
    
    try {
      const games = await findGamesBetweenPlayers(userDbId, otherUser.id, 10);
      
      const pendingGame = games?.find(game => {
        if (game.ended_at || game.started_at) return false;
        
        const isPlayer1 = game.player1_id === userDbId;
        const opponentReady = isPlayer1 
          ? game.player2_ready_to_start 
          : game.player1_ready_to_start;
        const iAmReady = isPlayer1 
          ? game.player1_ready_to_start 
          : game.player2_ready_to_start;
        
        return opponentReady && !iAmReady;
      });
      
      if (pendingGame) {
        console.log('📬 Found pending game request from opponent');
        setPendingGameRequest(pendingGame);
      } else {
        setPendingGameRequest(null);
      }
    } catch (error) {
      console.error('Error checking for pending game request:', error);
    }
  };

  // Create a new game and navigate to game mode
  const handleStartPlaying = async () => {
    if (!currentUserDbId || !otherUser?.id) {
      showToast('Unable to start game. Please try again.', 'error');
      return;
    }

    setCreatingGame(true);
    try {
      // First check if there's already a game (started or not)
      const existingGames = await findGamesBetweenPlayers(currentUserDbId, otherUser.id, 10);
      
      // Check if game is already started
      const startedGame = existingGames?.find(g => g.started_at && !g.ended_at);
      if (startedGame) {
        // Game already started - just navigate to it
        setActiveGameId(startedGame.id);
        setGameInProgress(true);
        showToast('Game already in progress!', 'info');
        
        navigation?.navigate('Clock', {
          gameId: startedGame.id,
          opponentUser: otherUser,
          isPlayer1: startedGame.player1_id === currentUserDbId,
        });
        setCreatingGame(false);
        return;
      }
      
      // Check for existing game that hasn't started yet
      const existingGame = existingGames?.find(g => !g.ended_at && !g.started_at);
      
      if (existingGame) {
        const isPlayer1 = existingGame.player1_id === currentUserDbId;
        const myReadyField = isPlayer1 ? 'player1_ready_to_start' : 'player2_ready_to_start';
        const opponentReadyField = isPlayer1 ? 'player2_ready_to_start' : 'player1_ready_to_start';
        
        // Check if opponent is already ready
        const opponentReady = isPlayer1 
          ? existingGame.player2_ready_to_start 
          : existingGame.player1_ready_to_start;
        
        // Mark myself as ready
        const updates = { [myReadyField]: true };
        
        // If both ready, start the game immediately
        if (opponentReady) {
          updates.started_at = new Date().toISOString();
        }
        
        await updateGame(existingGame.id, updates);
        
        setActiveGameId(existingGame.id);
        
        if (opponentReady) {
          setGameInProgress(true);
          showToast('Game started!', 'success');
        } else {
          showToast('Game request sent!', 'success');
        }
        
        navigation?.navigate('Clock', {
          gameId: existingGame.id,
          opponentUser: otherUser,
          isPlayer1,
        });
      } else {
        // Create new game
        const gameData = {
          player1_id: currentUserDbId,
          player2_id: otherUser.id,
          game_type: 'CASUAL',
          time_format: 'BLITZ',
          player1_ready_to_start: true,
          player2_ready_to_start: false,
          conversation_id: conversation?.id,
        };

        const newGame = await createGame(gameData);
        console.log('🎮 Created new game:', newGame.id);
        
        setActiveGameId(newGame.id);
        showToast('Game request sent!', 'success');
        
        navigation?.navigate('Clock', {
          gameId: newGame.id,
          opponentUser: otherUser,
          isPlayer1: true,
        });
      }
    } catch (error) {
      console.error('Error starting game:', error);
      showToast('Failed to start game. Please try again.', 'error');
    } finally {
      setCreatingGame(false);
    }
  };

  // Accept opponent's game request
  const handleAcceptGameRequest = async () => {
    if (!pendingGameRequest || !currentUserDbId) return;

    setCreatingGame(true);
    try {
      const isPlayer1 = pendingGameRequest.player1_id === currentUserDbId;
      await updateGame(pendingGameRequest.id, {
        [isPlayer1 ? 'player1_ready_to_start' : 'player2_ready_to_start']: true,
      });
      
      setActiveGameId(pendingGameRequest.id);
      setPendingGameRequest(null);
      showToast('Game request accepted!', 'success');
      
      navigation?.navigate('Clock', {
        gameId: pendingGameRequest.id,
        opponentUser: otherUser,
        isPlayer1,
      });
    } catch (error) {
      console.error('Error accepting game request:', error);
      showToast('Failed to accept game request.', 'error');
    } finally {
      setCreatingGame(false);
    }
  };

  // Update currentUserDbId when appUser changes
  useEffect(() => {
    if (appUser?.id && appUser.id !== currentUserDbId) {
      console.log('✅ Updating currentUserDbId from appUser:', appUser.id);
      setCurrentUserDbId(appUser.id);
    }
  }, [appUser?.id]);

  useEffect(() => {
    setHeaderAvatarError(false);
  }, [otherUser?.id, otherUser?.profilePictureUrl, otherUser?.profile_picture_url]);

  const fetchCurrentUserDbId = async () => {
    try {
      // Use appUser.id directly if available (Supabase database ID)
      if (appUser?.id) {
        console.log('✅ Using appUser.id:', appUser.id);
        setCurrentUserDbId(appUser.id);
        return appUser.id;
      }

      // Fallback: fetch by Clerk ID if appUser not loaded yet
      if (clerkUserId) {
        console.log('🔄 Fetching user by Clerk ID:', clerkUserId);
        const dbUser = await getUserByClerkId(clerkUserId);
        if (dbUser?.id) {
          console.log('✅ Fetched user DB ID:', dbUser.id);
          setCurrentUserDbId(dbUser.id);
          return dbUser.id;
        }
      }

      console.warn('⚠️ No user DB ID available');
      return null;
    } catch (error) {
      console.error('Error fetching current user DB ID:', error);
      return null;
    }
  };

  const checkForActiveGame = async (userDbId) => {
    if (!otherUser?.id) return;
    
    try {
      // Use Supabase to find active games
      const games = await findGamesBetweenPlayers(userDbId, otherUser.id, 30); // Check last 30 minutes

      const TIMEOUT_MS = 30 * 60 * 1000;
      const now = new Date();
      
      // Check for timed out games and cancel them
      for (const game of games || []) {
        if (game.started_at && !game.ended_at) {
          const gameStartTime = new Date(game.started_at);
          const timeSinceStart = now - gameStartTime;
          
          if (timeSinceStart > TIMEOUT_MS && !game.result) {
            await cancelTimedOutGame(game.id);
            continue;
          }
        }
      }

      // Find active game (started but not ended)
      const activeGame = games?.find(g => {
        if (g.ended_at || g.result) return false;
        // Game is active if it's started
        if (g.started_at) {
          const gameStartTime = new Date(g.started_at);
          const timeSinceStart = now - gameStartTime;
          return timeSinceStart <= TIMEOUT_MS;
        }
        return false;
      });

      // Also check for games that haven't started yet but exist
      const pendingGame = games?.find(g => !g.ended_at && !g.started_at);

      if (activeGame) {
        // Game is started and in progress
        setActiveGameId(activeGame.id);
        setGameInProgress(true);
        console.log('✅ Game in progress detected:', activeGame.id);
      } else if (pendingGame) {
        // Game exists but not started yet
        setActiveGameId(pendingGame.id);
        setGameInProgress(false);
      } else {
        setActiveGameId(null);
        setGameInProgress(false);
      }
    } catch (error) {
      console.error('Error checking for active game:', error);
    }
  };

  const cancelTimedOutGame = async (gameId) => {
    try {
      await updateGame(gameId, {
        ended_at: new Date().toISOString(),
        result: 'CANCELLED',
      });
    } catch (error) {
      console.error('Error cancelling timed out game:', error);
    }
  };

  const fetchMessages = async (convId) => {
    if (!convId || convId.startsWith('draft-')) {
      if (convId.startsWith('draft-')) {
        console.log('📝 Draft conversation - no messages to fetch');
      } else {
        console.error('❌ Cannot fetch messages: conversationId is missing');
      }
      return;
    }

    try {
      console.log('📥 Fetching messages for conversation:', convId);
      
      // First, try to use cached data for instant display
      const cachedData = getCachedConversation(convId);
      if (cachedData && cachedData.messages && cachedData.messages.length > 0) {
        console.log('⚡ Using cached messages for instant display:', cachedData.messages.length);
        const sortedMessages = cachedData.messages.sort(
          (a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at)
        );
        setMessages(sortedMessages);
      }

      // Fetch messages from Supabase
      const supabaseMessages = await getMessages(convId, 100);
      console.log('📨 Fetched fresh messages from Supabase:', {
        count: supabaseMessages.length,
        conversationId: convId,
        sample: supabaseMessages[0] ? {
          id: supabaseMessages[0].id,
          content: supabaseMessages[0].content?.substring(0, 30),
          senderId: supabaseMessages[0].sender_id,
        } : null,
      });
      
      if (!supabaseMessages || supabaseMessages.length === 0) {
        console.log('⚠️ No messages found in database for conversation:', convId);
        setMessages([]);
        return;
      }
      
      // Convert Supabase format (snake_case) to expected format (camelCase)
      const formattedMessages = supabaseMessages.map(msg => ({
        id: msg.id,
        conversationId: msg.conversation_id,
        senderId: msg.sender_id,
        receiverId: msg.receiver_id,
        content: msg.content,
        createdAt: msg.created_at,
        read: msg.read,
      }));
      
      // Sort by createdAt locally (oldest first)
      const sortedMessages = formattedMessages.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      
      console.log('✅ Setting messages:', sortedMessages.length);
      setMessages(sortedMessages);
      
      // Scroll to bottom after loading
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: false });
        // After scrolling to bottom, mark messages as read
        setTimeout(() => {
          markMessagesAsReadOnScroll();
        }, 200);
      }, 100);
    } catch (error) {
      console.error('❌ Error fetching messages:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        conversationId: convId,
      });
    }
  };

  const subscribeToMessages = (convId) => {
    // Clean up existing subscription if any
    if (subscriptionRef.current) {
      console.log('🧹 Cleaning up existing subscription');
      // If it's a Supabase unsubscribe function, call it
      if (typeof subscriptionRef.current === 'function') {
        subscriptionRef.current();
      } else if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
        subscriptionRef.current.unsubscribe();
      }
      subscriptionRef.current = null;
    }

    if (!convId) {
      console.error('❌ Cannot subscribe: conversationId is missing');
      return;
    }

    try {
      // Use Supabase real-time subscription
      const unsubscribe = subscribeToConversation(convId, (newMessage) => {
        console.log('🔔 New message received:', newMessage);
        
        // Convert Supabase format to expected format
        const formattedMessage = {
          id: newMessage.id,
          conversationId: newMessage.conversation_id,
          senderId: newMessage.sender_id,
          receiverId: newMessage.receiver_id,
          content: newMessage.content,
          createdAt: newMessage.created_at,
          read: newMessage.read,
        };
        
        setMessages((prevMessages) => {
          // Avoid duplicates
          if (prevMessages.find(m => m.id === formattedMessage.id)) {
            return prevMessages;
          }
          return [...prevMessages, formattedMessage];
        });
        
        // Mark as read if I'm the receiver (using database ID)
        // The scroll handler will mark messages as read when user scrolls to bottom
        // For now, we'll let the scroll handler manage read status
        
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
      });
      
      subscriptionRef.current = unsubscribe;
      console.log('✅ Successfully subscribed to messages for conversation via Supabase:', convId);
    } catch (error) {
      console.error('❌ Error setting up Supabase subscription:', error);
    }
  };

  const handleKeyPress = (e) => {
    // Web: Send message on Enter (but not Shift+Enter for new lines)
    if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const insertEmoji = (emoji) => {
    setInputText(prev => prev + emoji);
  };

  // Helper function to mark messages as read when at bottom
  const markMessagesAsReadOnScroll = async () => {
    if (!conversation || !currentUserDbId) return;

    try {
      // Check if there are any unread messages
      const unreadMessages = messages.filter(
        msg => msg.receiverId === currentUserDbId && msg.read === false
      );

      if (unreadMessages.length > 0) {
        console.log(`📬 Marking ${unreadMessages.length} messages as read`);
        await markMessagesAsRead(conversation.id, currentUserDbId);
        
        // Update local state to reflect read status
        setMessages(prev => prev.map(msg => 
          msg.receiverId === currentUserDbId && !msg.read 
            ? { ...msg, read: true }
            : msg
        ));
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleScroll = (event) => {
    if (!conversation || !currentUserDbId) return;

    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const scrollPosition = contentOffset.y;
    const contentHeight = contentSize.height;
    const containerHeight = layoutMeasurement.height;
    
    // Calculate distance from bottom (with 150px threshold for "near bottom")
    const distanceFromBottom = contentHeight - scrollPosition - containerHeight;
    const isNearBottom = distanceFromBottom < 150;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // If user is near bottom, mark messages as read after a short delay (debounce)
    if (isNearBottom) {
      scrollTimeoutRef.current = setTimeout(() => {
        markMessagesAsReadOnScroll();
      }, 500); // 500ms debounce to avoid too frequent calls
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || sending || !conversation || !currentUserDbId || !otherUser) {
      console.warn('⚠️ Cannot send message - missing required data:', {
        hasText: !!inputText.trim(),
        sending,
        hasConversation: !!conversation,
        hasCurrentUserDbId: !!currentUserDbId,
        hasOtherUser: !!otherUser,
      });
      return;
    }
    setShowEmojiPicker(false);

    const messageContent = inputText.trim();
    console.log('📤 Sending message:', {
      conversationId: conversation.id,
      senderId: currentUserDbId,
      receiverId: otherUser.id,
      content: messageContent.substring(0, 50) + '...',
    });

    try {
      setSending(true);

      // Use Supabase to send message (will create conversation if it's a draft)
      const newMessage = await sendMessageSupabase(
        conversation.id,
        currentUserDbId,
        otherUser.id,
        messageContent
      );

      // If conversation was a draft, update it with the real conversation ID
      if (conversation.id.startsWith('draft-') && newMessage.conversation_id) {
        console.log('✅ Conversation created, updating conversation ID:', newMessage.conversation_id);
        setCurrentConversationId(newMessage.conversation_id);
        
        // Now fetch messages and subscribe to the real conversation
        await fetchMessages(newMessage.conversation_id);
        subscribeToMessages(newMessage.conversation_id);
      }

      if (!newMessage || !newMessage.id) {
        throw new Error('Message was not created - no ID returned');
      }

      console.log('✅ Message sent via Supabase:', {
        id: newMessage.id,
        conversationId: newMessage.conversation_id,
        senderId: newMessage.sender_id,
        receiverId: newMessage.receiver_id,
      });

      // Convert Supabase format to expected format and add to messages
      const formattedMessage = {
        id: newMessage.id,
        conversationId: newMessage.conversation_id,
        senderId: newMessage.sender_id,
        receiverId: newMessage.receiver_id,
        content: newMessage.content,
        createdAt: newMessage.created_at,
        read: newMessage.read,
      };

      // Add to local state immediately for optimistic UI
      setMessages(prev => {
        // Avoid duplicates
        if (prev.find(m => m.id === formattedMessage.id)) {
          return prev;
        }
        return [...prev, formattedMessage];
      });

      // Scroll to bottom
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
        // Mark messages as read after scrolling to bottom
        setTimeout(() => {
          markMessagesAsReadOnScroll();
        }, 200);
      }, 100);

      // Send push notification
      try {
        // Get receiver data from Supabase
        const receiver = await getUserById(otherUser.id);
        if (receiver?.push_token) {
          const senderName = user?.username || appUser?.username || 'Someone';
          await NotificationService.sendPushNotification(
            receiver.push_token,
            senderName,
            messageContent
          );
        }
      } catch (error) {
        console.error('Error sending push notification:', error);
        // Don't fail the message send if notification fails
      }

      setInputText('');
    } catch (error) {
      console.error('❌ Error sending message:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        conversationId: conversation?.id,
        senderId: currentUserDbId,
        receiverId: otherUser?.id,
      });
      // Show error to user
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const renderMessage = ({ item }) => {
    const isMyMessage = item.senderId === currentUserDbId;

    if (!messageAnimations.current[item.id]) {
      messageAnimations.current[item.id] = new Animated.Value(0);
      
      Animated.spring(messageAnimations.current[item.id], {
        toValue: 1,
        useNativeDriver: true,
        tension: 80,
        friction: 8,
      }).start();
    }

    const slideUpAnimation = {
      transform: [
        {
          translateY: messageAnimations.current[item.id].interpolate({
            inputRange: [0, 1],
            outputRange: [50, 0],
          }),
        },
      ],
      opacity: messageAnimations.current[item.id],
    };

    const timeStr = new Date(item.createdAt).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <Animated.View
        style={[
          styles.messageRow,
          isMyMessage ? styles.messageRowMine : styles.messageRowTheirs,
          slideUpAnimation,
        ]}
      >
        {isMyMessage && (
          <Text style={[styles.timestampSide, styles.timestampLeft]}>{timeStr}</Text>
        )}
        <View
          style={[
            styles.messageContainer,
            isMyMessage ? styles.myMessage : styles.theirMessage,
          ]}
        >
          <Text style={[
            styles.messageText,
            isMyMessage ? styles.myMessageText : styles.theirMessageText,
          ]}>
            {item.content}
          </Text>
        </View>
        {!isMyMessage && (
          <Text style={[styles.timestampSide, styles.timestampRight]}>{timeStr}</Text>
        )}
      </Animated.View>
    );
  };

  const styles = getStyles(colors, shadows, isDarkMode);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        {isMobile && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (onBack) {
                onBack();
              } else if (navigation) {
                navigation.goBack();
              }
            }}
          >
            <Icon name="arrow-back" size={24} color={colors.text} />
          </TouchableOpacity>
        )}
        <View style={styles.headerInfo}>
          <View style={styles.headerTitleRow}>
            {(otherUser?.profilePictureUrl || otherUser?.profile_picture_url) && !headerAvatarError ? (
              <Image
                source={{ uri: getProfilePictureUrl(otherUser.profilePictureUrl || otherUser.profile_picture_url, 'thumbnail') }}
                style={styles.headerAvatar}
                resizeMode="cover"
                onError={() => setHeaderAvatarError(true)}
              />
            ) : null}
            <Text style={styles.headerTitle} numberOfLines={1}>
              {otherUser?.username || otherUser?.clubName || 'Chat'}
            </Text>
          </View>
          {!isMobile && (
            <Text style={styles.headerSubtitle}>
              {otherUser?.accountType === 'CLUB' ? 'Club' : 'Player'}
            </Text>
          )}
        </View>
        <View style={styles.headerActions}>
          {isMobile && !activeGameId && !gameInProgress && !pendingGameRequest && (
            <TouchableOpacity
              style={styles.headerPlayButton}
              onPress={handleStartPlaying}
              disabled={creatingGame || !currentUserDbId}
              activeOpacity={0.8}
            >
              <Icon name="sports-esports" size={16} color="white" />
              <Text style={styles.headerPlayButtonText}>{creatingGame ? '...' : 'Play'}</Text>
            </TouchableOpacity>
          )}
          {isMobile && !activeGameId && !gameInProgress && pendingGameRequest && (
            <TouchableOpacity
              style={styles.headerAcceptButton}
              onPress={handleAcceptGameRequest}
              disabled={creatingGame || !currentUserDbId}
              activeOpacity={0.8}
            >
              <Icon name="check-circle" size={16} color="white" />
              <Text style={styles.headerPlayButtonText}>Accept</Text>
            </TouchableOpacity>
          )}
          {Platform.OS !== 'web' && (
            <TouchableOpacity
              style={styles.headerButton}
              onPress={() => navigation.navigate('Profile', { userId: otherUser.id })}
            >
              <Icon name="info-outline" size={24} color={colors.text} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Game Tracking */}
      {activeGameId && (
        <View style={{ paddingHorizontal: spacing.md }}>
          <GameTrackingView
            gameId={activeGameId}
            myUserId={currentUserDbId}
            opponentUserId={otherUser.id}
            onGameComplete={() => {
              setGameInProgress(false);
              setActiveGameId(null);
            }}
          />
        </View>
      )}

      {/* Game In Progress Banner */}
      {gameInProgress && activeGameId && (
        <View style={styles.gameInProgressBanner}>
          <Icon name="sports-esports" size={20} color={colors.primary} />
          <Text style={styles.gameInProgressText}>
            Game in progress • Tap to view
          </Text>
          <TouchableOpacity
            onPress={() => {
              const isPlayer1 = activeGameId ? true : false; // Will be determined from game data
              navigation?.navigate('Clock', {
                gameId: activeGameId,
                opponentUser: otherUser,
                isPlayer1,
              });
            }}
            style={styles.viewGameButton}
          >
            <Text style={styles.viewGameButtonText}>View</Text>
          </TouchableOpacity>
        </View>
      )}


      {/* Start Playing / Accept Request Buttons — desktop only (mobile shows in header) */}
      {!isMobile && !activeGameId && !gameInProgress && (
        <View style={{ paddingHorizontal: spacing.md, paddingVertical: spacing.sm }}>
          {pendingGameRequest ? (
            <TouchableOpacity
              style={styles.acceptRequestButton}
              onPress={handleAcceptGameRequest}
              disabled={creatingGame || !currentUserDbId}
              activeOpacity={0.8}
            >
              <Icon name="check-circle" size={20} color="white" />
              <Text style={styles.acceptRequestText}>
                {creatingGame ? 'Accepting...' : 'Accept Game Request'}
              </Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={styles.startPlayingButton}
              onPress={handleStartPlaying}
              disabled={creatingGame || !currentUserDbId}
              activeOpacity={0.8}
            >
              <Icon name="sports-esports" size={20} color="white" />
              <Text style={styles.startPlayingText}>
                {creatingGame ? 'Starting...' : 'Start Playing'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Messages */}
      <FlatList
        ref={flatListRef}
        data={messages}
        keyExtractor={(item) => item.id}
        renderItem={renderMessage}
        contentContainerStyle={styles.messagesList}
        style={styles.messagesContainer}
        showsVerticalScrollIndicator={true}
        onContentSizeChange={() => {
          flatListRef.current?.scrollToEnd({ animated: false });
          // Mark as read after scrolling
          setTimeout(() => markMessagesAsReadOnScroll(), 200);
        }}
        onLayout={() => {
          flatListRef.current?.scrollToEnd({ animated: false });
          // Mark as read after scrolling
          setTimeout(() => markMessagesAsReadOnScroll(), 200);
        }}
        onScroll={handleScroll}
        scrollEventThrottle={400}
      />

      {/* Emoji Picker */}
      {showEmojiPicker && !gameInProgress && (
        <View style={styles.emojiPickerContainer}>
          {QUICK_EMOJIS.map((emoji, index) => (
            <TouchableOpacity
              key={index}
              style={styles.emojiButton}
              onPress={() => insertEmoji(emoji)}
            >
              <Text style={styles.emojiText}>{emoji}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Input */}
      {!gameInProgress && (
        <View style={styles.inputContainer}>
          <TouchableOpacity
            style={styles.emojiToggleButton}
            onPress={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            <Icon 
              name={showEmojiPicker ? 'keyboard' : 'emoji-emotions'} 
              size={isMobile ? 20 : 24} 
              color={showEmojiPicker ? colors.primary : colors.textSecondary} 
            />
          </TouchableOpacity>
          <TextInput
            style={styles.input}
            placeholder="Type a message..."
            placeholderTextColor={colors.textSecondary}
            value={inputText}
            onChangeText={setInputText}
            onKeyPress={handleKeyPress}
            multiline
            maxLength={1000}
            blurOnSubmit={false}
          />
          <TouchableOpacity
            style={[styles.sendButton, (!inputText.trim() || sending) && styles.sendButtonDisabled]}
            onPress={sendMessage}
            disabled={!inputText.trim() || sending}
          >
            {sending ? (
              <LoadingAnimation size={50} />
            ) : (
              <Icon name="send" size={isMobile ? 18 : 22} color="white" />
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

// Dynamic styles based on theme
const getStyles = (colors, shadows, isDarkMode) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    ...(Platform.OS === 'web' ? {
      backgroundImage: `url("data:image/svg+xml;base64,${btoa(`
        <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
          <rect width="30" height="30" x="0" y="0" fill="${isDarkMode ? 'rgba(255, 255, 255, 0.015)' : 'rgba(0, 0, 0, 0.015)'}"/>
          <rect width="30" height="30" x="30" y="0" fill="transparent"/>
          <rect width="30" height="30" x="0" y="30" fill="transparent"/>
          <rect width="30" height="30" x="30" y="30" fill="${isDarkMode ? 'rgba(255, 255, 255, 0.015)' : 'rgba(0, 0, 0, 0.015)'}"/>
        </svg>
      `)}")`,
      backgroundSize: '60px 60px',
      backgroundRepeat: 'repeat',
      backgroundPosition: '0 0',
    } : {}),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  backButton: {
    marginRight: spacing.sm,
    padding: spacing.xs,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: typography.h6,
    fontWeight: typography.bold,
    color: colors.text,
    flex: 1,
  },
  headerSubtitle: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerButton: {
    padding: spacing.xs,
  },
  headerPlayButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.primary,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: borderRadius.pill,
  },
  headerAcceptButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#FFD700',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: borderRadius.pill,
  },
  headerPlayButtonText: {
    fontSize: 13,
    fontWeight: typography.semiBold,
    color: 'white',
  },
  messagesContainer: {
    flex: 1,
  },
  messagesList: {
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: spacing.sm,
    paddingBottom: spacing.xl,
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 6,
  },
  messageRowMine: {
    justifyContent: 'flex-end',
  },
  messageRowTheirs: {
    justifyContent: 'flex-start',
  },
  messageContainer: {
    maxWidth: '72%',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
  },
  myMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#84D288',
    borderBottomRightRadius: 4,
    shadowColor: '#7FB690',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.6,
    shadowRadius: 0,
    elevation: 2,
  },
  theirMessage: {
    alignSelf: 'flex-start',
    backgroundColor: colors.surface,
    borderBottomLeftRadius: 4,
    shadowColor: isDarkMode ? '#000' : '#D3D3D3',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: isDarkMode ? 0.3 : 0.6,
    shadowRadius: 0,
    elevation: 2,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 19,
    fontWeight: typography.regular,
  },
  myMessageText: {
    color: colors.textInverse,
  },
  theirMessageText: {
    color: colors.text,
  },
  timestampSide: {
    fontSize: 10,
    color: colors.textLight || colors.textSecondary,
    marginBottom: 2,
  },
  timestampLeft: {
    marginRight: 5,
  },
  timestampRight: {
    marginLeft: 5,
  },
  emojiPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    gap: spacing.xs,
  },
  emojiButton: {
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    backgroundColor: colors.backgroundDark,
  },
  emojiText: {
    fontSize: 22,
  },
  inputContainer: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    alignItems: 'center',
    gap: 6,
  },
  emojiToggleButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    backgroundColor: colors.backgroundDark,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 6,
    minHeight: 32,
    maxHeight: 36,
    fontSize: 14,
    color: colors.text,
    textAlignVertical: 'center',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  sendButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: colors.textLight,
  },
  startPlayingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: colors.primary,
    paddingVertical: spacing.md + 4,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.lg,
    ...shadows.medium,
    minHeight: 50,
  },
  startPlayingText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: 'white',
  },
  acceptRequestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFD700',
    paddingVertical: spacing.md + 4,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.lg,
    ...shadows.medium,
    minHeight: 50,
  },
  acceptRequestText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: 'white',
  },
  gameInProgressBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    backgroundColor: colors.primaryLight + '20',
    borderBottomWidth: 1,
    borderBottomColor: colors.primaryLight,
    gap: spacing.sm,
  },
  gameInProgressText: {
    flex: 1,
    fontSize: typography.bodySmall,
    fontWeight: typography.semiBold,
    color: colors.primaryDark,
  },
  viewGameButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
  },
  viewGameButtonText: {
    fontSize: typography.bodySmall,
    fontWeight: typography.semiBold,
    color: 'white',
  },
});

export default ChatDetailPanel;

